import type { ContactType, Product, QuoteLine, QuoteStatus, QuoteSummary, QuoteTotals } from "../../lib/types";

/** What the customer is told: COT-0042. */
export const quoteNumber = (n: number) => `COT-${String(n).padStart(4, "0")}`;

export const STATUS_LABEL: Record<QuoteStatus | "expired", string> = {
  draft: "Borrador",
  sent: "Enviada",
  expired: "Vencida",
  accepted: "Aceptada",
  rejected: "Rechazada",
};

export const STATUS_PILL: Record<QuoteStatus | "expired", string> = {
  draft: "pill-warn",
  sent: "pill-human",
  expired: "pill-closed",
  accepted: "pill-bot",
  rejected: "pill-closed",
};

/** The status the panel shows: a sent quote past its date is expired. */
export const shownStatus = (q: Pick<QuoteSummary, "status" | "expired">) => (q.expired ? "expired" : q.status);

/** ₡25 000 or US$35,50, as on the PDF. */
export function money(n: number, currency: "CRC" | "USD") {
  const whole = Number.isInteger(n);
  const text = new Intl.NumberFormat("es-CR", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 })
    .format(n)
    .replace(/\s/g, " ");
  return currency === "USD" ? `US$${text}` : `₡${text}`;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export const lineTotal = (l: Pick<QuoteLine, "quantity" | "unitPrice" | "discount">) =>
  round2(l.quantity * l.unitPrice * (1 - l.discount / 100));

/** Same math as the API (core/quotes.ts), so totals update while typing. */
export function quoteTotals(lines: QuoteLine[], pricesIncludeTax: boolean, taxRate: number): QuoteTotals {
  const sum = round2(lines.reduce((acc, l) => acc + lineTotal(l), 0));
  const gross = round2(lines.reduce((acc, l) => acc + l.quantity * l.unitPrice, 0));
  const discount = round2(gross - sum);
  if (pricesIncludeTax) {
    const tax = round2((sum * taxRate) / (100 + taxRate));
    return { gross, discount, subtotal: round2(sum - tax), tax, total: sum };
  }
  const tax = round2((sum * taxRate) / 100);
  return { gross, discount, subtotal: sum, tax, total: round2(sum + tax) };
}

/** A catalog item as a quote line, at the price for this customer's type when it has one. */
export function lineFromProduct(p: Product, customerType: ContactType | null): QuoteLine {
  const price =
    (customerType === "business" ? p.priceBusiness : customerType === "institution" ? p.priceInstitution : null) ??
    p.price ??
    0;
  return { productId: p.id, description: p.name, model: p.model, quantity: 1, unitPrice: price, discount: 0 };
}

/** 24 oct. 2026 from YYYY-MM-DD. */
export const shortDate = (ymd: string) =>
  new Date(`${ymd}T12:00:00Z`).toLocaleDateString("es-CR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
